import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  FiBarChart2,
  FiRefreshCw,
  FiCalendar,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiTrendingUp,
  FiLayers,
  FiPackage,
} from "react-icons/fi";

import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { translate } from "../../../hooks/translate";
import { withAuth } from "../../../components/HOC/withAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import CardInfo from "../../../components/CustomUI/CardInfo";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import DatePicker from "../../../components/CustomUI/DatePicker";
import ApexChart from "../../../components/CustomUI/Charts/ApexChart";

import styles from "./ProductionLogs.module.scss";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import {
  APP_TIMEZONE,
  dateStrInAppTz,
  formatInstantDateShortTr,
  noonInAppTzDate,
} from "../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ProductionLogs = () => {
  const { getToken } = useAuth();
  useTitle("Rovion MES - Üretim Geçmişi");

  const [items, setItems] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Date period (like DeviceMonitor)
  const [periodView, setPeriodView] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [tempDateRange, setTempDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Filters
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [productSearch, setProductSearch] = useState("");

  const toISODate = (d) => {
    if (!d) return "";
    return dateStrInAppTz(new Date(d));
  };

  const formatDateForPicker = (date) => {
    if (!date) return "";
    const s = dateStrInAppTz(new Date(date));
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  };

  const parseDateFromPicker = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("/");
    return new Date(year, month - 1, day);
  };

  const { fromDate, toDate } = useMemo(() => {
    const now = new Date();
    if (periodView === "day") {
      const d = new Date(selectedDate);
      const iso = toISODate(d);
      return { fromDate: iso, toDate: iso };
    }
    if (periodView === "week") {
      const dayOfWeek = now.getDay();
      const start = new Date(now);
      start.setDate(now.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      return { fromDate: toISODate(start), toDate: toISODate(now) };
    }
    if (periodView === "month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { fromDate: toISODate(start), toDate: toISODate(end) };
    }
    if (
      periodView === "custom" &&
      customDateRange.startDate &&
      customDateRange.endDate
    ) {
      return {
        fromDate: toISODate(customDateRange.startDate),
        toDate: toISODate(customDateRange.endDate),
      };
    }
    const today = toISODate(now);
    return { fromDate: today, toDate: today };
  }, [periodView, selectedDate, customDateRange]);

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (newDate <= today) setSelectedDate(newDate);
  };

  const handleCustomPeriodSelect = () => {
    setTempDateRange({
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    });
    setShowDatePicker(true);
  };

  const handleStartDateChange = (dateString) => {
    const date = parseDateFromPicker(dateString);
    setTempDateRange((prev) => ({ ...prev, startDate: date }));
  };

  const handleEndDateChange = (dateString) => {
    const date = parseDateFromPicker(dateString);
    setTempDateRange((prev) => ({ ...prev, endDate: date }));
  };

  const applyDateRange = () => {
    if (tempDateRange.startDate && tempDateRange.endDate) {
      setCustomDateRange({
        startDate: tempDateRange.startDate,
        endDate: tempDateRange.endDate,
      });
      setPeriodView("custom");
      setShowDatePicker(false);
    }
  };

  const clearCustomDateRange = () => {
    setCustomDateRange({ startDate: null, endDate: null });
    setTempDateRange({ startDate: null, endDate: null });
    setPeriodView("day");
    setShowDatePicker(false);
  };

  const cancelDateRange = () => {
    setTempDateRange({
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    });
    if (!customDateRange.startDate || !customDateRange.endDate) {
      setPeriodView("day");
    }
    setShowDatePicker(false);
  };

  const addToast = (message, type = "info") => {
    const t = { id: Date.now(), message, type };
    setToasts((p) => [...p, t]);
    setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 3000);
  };

  const h = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchLines = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/production-lines`, {
        headers: h(),
      });
      const arr = res.data?.lines || res.data || [];
      setLines(Array.isArray(arr) ? arr : []);
    } catch {
      setLines([]);
    }
  }, [getToken]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (fromDate) params.set("from", fromDate);
      if (toDate) params.set("to", toDate);
      if (selectedLineId) params.set("lineId", selectedLineId);
      const res = await axios.get(`${API_URL}/api/daily-production?${params}`, {
        headers: h(),
      });
      setItems(res.data?.items || []);
    } catch (err) {
      addToast(err.response?.data?.message || "Veriler yüklenemedi", "danger");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, fromDate, toDate, selectedLineId]);

  useEffect(() => {
    fetchLines();
  }, [fetchLines]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const formatDate = (d) => {
    if (!d) return "—";
    return formatInstantDateShortTr(d);
  };

  const displayLine = (item) =>
    item.lineId
      ? item.lineName || String(item.lineId) || "—"
      : item.lineName || "—";

  const displayOutput = (item) => {
    const out = item.outputItemId;
    if (!out) return "—";
    if (typeof out === "object" && (out.name || out.code)) {
      return `${out.name || ""} (${out.code || ""})`.trim() || "—";
    }
    return "—";
  };

  // Filtered items (client-side product search)
  const filteredItems = useMemo(() => {
    if (!productSearch?.trim()) return items;
    const q = productSearch.toLowerCase();
    return items.filter((item) => {
      const out = displayOutput(item);
      return out?.toLowerCase().includes(q);
    });
  }, [items, productSearch]);

  // KPI stats
  const stats = useMemo(() => {
    const totalQty = filteredItems.reduce((s, i) => s + (i.quantity ?? 0), 0);
    const uniqueDates = new Set(
      filteredItems.map((i) =>
        i.date ? dateStrInAppTz(new Date(i.date)) : "",
      ),
    ).size;
    const uniqueLines = new Set(
      filteredItems.map((i) => displayLine(i)).filter(Boolean),
    ).size;
    return { totalQty, uniqueDates, uniqueLines };
  }, [filteredItems]);

  // Chart: production per day (sorted by date)
  const dailyChartData = useMemo(() => {
    const byDate = {};
    filteredItems.forEach((item) => {
      if (!item.date) return;
      const iso = dateStrInAppTz(new Date(item.date));
      byDate[iso] = (byDate[iso] || 0) + (item.quantity ?? 0);
    });
    const sorted = Object.entries(byDate)
      .map(([iso, val]) => ({
        iso,
        label: new Intl.DateTimeFormat("tr-TR", {
          timeZone: APP_TIMEZONE,
          day: "2-digit",
          month: "short",
        }).format(noonInAppTzDate(iso)),
        val,
      }))
      .sort((a, b) => a.iso.localeCompare(b.iso));
    return {
      categories: sorted.map((s) => s.label),
      series: sorted.map((s) => s.val),
    };
  }, [filteredItems]);

  const lineOptions = useMemo(() => {
    const opts = [{ value: "", label: "Tüm hatlar" }];
    lines.forEach((l) => {
      opts.push({
        value: l._id,
        label: l.name || l.code || String(l._id),
      });
    });
    return opts;
  }, [lines]);

  const hasActiveFilters =
    selectedLineId ||
    productSearch?.trim() ||
    (periodView === "custom" &&
      customDateRange.startDate &&
      customDateRange.endDate);

  const clearFilters = () => {
    setSelectedLineId(null);
    setProductSearch("");
    clearCustomDateRange();
  };
  const defaultHeaders = [
    {
      name: "Tarih",
      dataType: "date",
      id: 0,
      customSorter: (a, b) =>
        new Date(a?.date || a?.createdAt || 0) -
        new Date(b?.date || b?.createdAt || 0),
    },
    {
      name: "Hat",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a?.lineName || a?.lineId?.name || "").localeCompare(
          String(b?.lineName || b?.lineId?.name || ""),
        ),
    },
    {
      name: "Ürün",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a?.productName || a?.partName || "").localeCompare(
          String(b?.productName || b?.partName || ""),
        ),
    },
    {
      name: "Miktar",
      dataType: "number",
      id: 3,
      customSorter: (a, b) => Number(a?.quantity || 0) - Number(b?.quantity || 0),
    },
  ];
  const defaultVisibleColumns = [0, 1, 2, 3];
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility(
      "production-logs-table",
      defaultHeaders,
      defaultVisibleColumns,
    );

  return (
    <AppPage
      title="Üretim Geçmişi"
      description="Günlük üretim analitiği ve kayıtlar"
      className={styles.page}
    >
      {/* KPI Cards */}
      <Container className={`mb-3 ${styles.kpiRow}`}>
        <ContainerItem sm={4} md={4} xl={3} className={styles.kpiCard}>
          <CardInfo
            card={{
              title: "Toplam Üretim",
              value: stats.totalQty,
              type: "number",
              icon: <FiTrendingUp style={{ color: "#4361ee" }} />,
            }}
            appearance="primary"
          />
        </ContainerItem>
        <ContainerItem sm={4} md={4} xl={3} className={styles.kpiCard}>
          <CardInfo
            card={{
              title: "Üretim Günü",
              value: stats.uniqueDates,
              type: "number",
              icon: <FiCalendar style={{ color: "#2ecc71" }} />,
            }}
            appearance="success"
          />
        </ContainerItem>
        <ContainerItem sm={4} md={4} xl={3} className={styles.kpiCard}>
          <CardInfo
            card={{
              title: "Aktif Hat",
              value: stats.uniqueLines,
              type: "number",
              icon: <FiLayers style={{ color: "#f39c12" }} />,
            }}
            appearance="warning"
          />
        </ContainerItem>
        <ContainerItem sm={4} md={4} xl={3} className={styles.kpiCard}>
          <CardInfo
            card={{
              title: "Toplam Kayıt",
              value: filteredItems.length,
              type: "number",
              icon: <FiPackage style={{ color: "#9b59b6" }} />,
            }}
            appearance="primary"
          />
        </ContainerItem>
      </Container>

      {/* Advanced Filters */}
      <CardContainer className="mb-3">
        <div
          className={styles.filterToggle}
          onClick={() => setFiltersOpen((o) => !o)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && setFiltersOpen((o) => !o)
          }
        >
          <span>
            <FiFilter size={16} className="mr-2" />
            Filtreler
          </span>
          {filtersOpen ? (
            <FiChevronUp size={20} />
          ) : (
            <FiChevronDown size={20} />
          )}
        </div>
        {filtersOpen && (
          <div className={styles.filterPanel}>
            <div className="row g-3 align-items-end mb-3 pb-3">
              <div className="col-md-6 col-lg-3">
                <label className="form-label small text-muted">
                  Üretim Hattı
                </label>
                <Select
                  options={lineOptions}
                  selected={selectedLineId ? [selectedLineId] : [""]}
                  onSelected={(vals) => {
                    const v = vals?.[0]?.value;
                    setSelectedLineId(v === "" || !v ? null : v);
                  }}
                  unique
                  placeholder="Tüm hatlar"
                  className="mb-0"
                />
              </div>
              <div className="col-md-6 col-lg-3">
                <label className="form-label small text-muted">Ürün Ara</label>
                <Input
                  type="text"
                  placeholder="Ürün adı veya kodu..."
                  value={productSearch}
                  onChange={(v) => setProductSearch(v)}
                />
              </div>
              <div className="col-12">
                <Button
                  appearance="danger-subtle"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Filtreleri Temizle
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContainer>

      {/* Per-day grouped table */}
      <CardContainer title="Günlük Kayıtlar">
        <div className={styles.periodFilterRow}>
          <div className={styles.periodSection}>
            {periodView === "custom" &&
              customDateRange.startDate &&
              customDateRange.endDate && (
                <div className={styles.customDateBadge}>
                  <span>
                    {formatDateForPicker(customDateRange.startDate)} -{" "}
                    {formatDateForPicker(customDateRange.endDate)}
                  </span>
                  <button
                    type="button"
                    className={styles.clearBadgeBtn}
                    onClick={clearCustomDateRange}
                    title={translate("UI.CLEAR_DATE_RANGE")}
                  >
                    <FiX size={12} />
                  </button>
                </div>
              )}
            <div className={styles.periodSelector}>
              {periodView === "day" && (
                <div className={styles.dayNavigation}>
                  <button
                    type="button"
                    className={styles.navDayBtn}
                    onClick={handlePreviousDay}
                    title={translate("UI.PREVIOUS_DAY")}
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <span className={styles.selectedDateLabel}>
                    {(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const selected = new Date(selectedDate);
                      selected.setHours(0, 0, 0, 0);
                      if (selected.getTime() === today.getTime()) {
                        return translate("UI.TODAY");
                      }
                      return formatDateForPicker(selectedDate);
                    })()}
                  </span>
                  <button
                    type="button"
                    className={styles.navDayBtn}
                    onClick={handleNextDay}
                    disabled={
                      new Date(selectedDate).toDateString() ===
                      new Date().toDateString()
                    }
                    title={translate("UI.NEXT_DAY")}
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              )}
              <button
                type="button"
                className={`${styles.periodBtn} ${periodView === "day" ? styles.active : ""}`}
                onClick={() => {
                  setPeriodView("day");
                  setShowDatePicker(false);
                }}
                title={translate("UI.DAY")}
              >
                {translate("UI.DAY")}
              </button>
              <button
                type="button"
                className={`${styles.periodBtn} ${periodView === "week" ? styles.active : ""}`}
                onClick={() => {
                  setPeriodView("week");
                  setShowDatePicker(false);
                }}
                title={translate("UI.THIS_WEEK")}
              >
                {translate("UI.WEEK")}
              </button>
              <button
                type="button"
                className={`${styles.periodBtn} ${periodView === "month" ? styles.active : ""}`}
                onClick={() => {
                  setPeriodView("month");
                  setShowDatePicker(false);
                }}
                title={translate("UI.THIS_MONTH")}
              >
                {translate("UI.MONTH")}
              </button>
              <button
                type="button"
                className={`${styles.periodBtn} ${periodView === "custom" ? styles.active : ""}`}
                onClick={handleCustomPeriodSelect}
                title={translate("UI.CUSTOM_DATE_RANGE")}
              >
                <FiCalendar size={14} /> {translate("UI.CUSTOM")}
              </button>
            </div>
            {showDatePicker && (
              <div className={styles.customDateRangePicker}>
                <div className={styles.dateRangeHeader}>
                  <span>{translate("UI.SELECT_DATE_RANGE")}</span>
                  <button
                    type="button"
                    className={styles.closeDatePicker}
                    onClick={cancelDateRange}
                  >
                    <FiX size={16} />
                  </button>
                </div>
                <div className={styles.dateRangeInputs}>
                  <div className={styles.dateInputGroup}>
                    <DatePicker
                      label={translate("UI.START_DATE")}
                      placeholder={translate("UI.SELECT_START_DATE")}
                      value={
                        tempDateRange.startDate
                          ? formatDateForPicker(tempDateRange.startDate)
                          : ""
                      }
                      maxDate={
                        tempDateRange.endDate
                          ? formatDateForPicker(tempDateRange.endDate)
                          : formatDateForPicker(new Date())
                      }
                      onChange={handleStartDateChange}
                    />
                  </div>
                  <div className={styles.dateInputGroup}>
                    <DatePicker
                      label={translate("UI.END_DATE")}
                      placeholder={translate("UI.SELECT_END_DATE")}
                      value={
                        tempDateRange.endDate
                          ? formatDateForPicker(tempDateRange.endDate)
                          : ""
                      }
                      minDate={
                        tempDateRange.startDate
                          ? formatDateForPicker(tempDateRange.startDate)
                          : undefined
                      }
                      maxDate={formatDateForPicker(new Date())}
                      onChange={handleEndDateChange}
                    />
                  </div>
                  <div className={styles.dateRangeActions}>
                    <button
                      type="button"
                      className={styles.applyDateRangeBtn}
                      onClick={applyDateRange}
                      disabled={
                        !tempDateRange.startDate || !tempDateRange.endDate
                      }
                      title={translate("UI.APPLY_DATE_RANGE")}
                    >
                      {translate("UI.APPLY")}
                    </button>
                    {(tempDateRange.startDate || tempDateRange.endDate) && (
                      <button
                        type="button"
                        className={styles.clearDateRangeBtn}
                        onClick={clearCustomDateRange}
                        title={translate("UI.CLEAR_DATE_RANGE")}
                      >
                        <FiX size={14} /> {translate("UI.CLEAR")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <div className="loading-container py-5">
            <FiBarChart2 className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center text-muted py-5">
            <FiCalendar size={48} className="mb-2" />
            <p>Üretim kaydı bulunamadı</p>
          </div>
        ) : (
          <Table
            columns={defaultHeaders}
            visibleColumns={visibleColumns}
            tableId="production-logs-table"
            checkboxSelection={false}
            loading={loading}
            hideFooter={true}
            viewOptions={
              <ListViewOptionsPanel
                columns={defaultHeaders}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                showApply={false}
                showReset={false}
              />
            }
            search={true}
          >
            {filteredItems.map((item) => (
              <Tr
                key={`${item.date}-${item.outputItemId?._id || item.outputItemId}-${item.lineId}`}
                data={item}
              >
                {visibleColumns.includes(0) && (
                  <Td>{formatDate(item.date)}</Td>
                )}
                {visibleColumns.includes(1) && (
                  <Td>{displayLine(item)}</Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>{displayOutput(item)}</Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>
                    <strong>{item.quantity ?? 0}</strong> adet
                  </Td>
                )}
              </Tr>
            ))}
          </Table>
        )}
      </CardContainer>

      {/* Daily Chart */}
      {filteredItems.length > 0 && dailyChartData.categories.length > 0 && (
        <CardContainer className="mt-3" title="Günlük Üretim Miktarı">
          <ApexChart
            type="bar"
            series={[{ name: "Miktar", data: dailyChartData.series }]}
            categories={dailyChartData.categories}
            colors={["#4361ee"]}
            options={{
              chart: { height: 300 },
              plotOptions: {
                bar: {
                  borderRadius: 4,
                  columnWidth: "60%",
                },
              },
              xaxis: {
                labels: { rotate: -45 },
              },
              dataLabels: { enabled: false },
            }}
            height={300}
          />
        </CardContainer>
      )}

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(ProductionLogs, ["admin", "employee", "user"]);
